import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Loader2, Search } from 'lucide-react'
import { APXFieldsEditor } from './APXFieldsEditor'
import { useAccounts, useCreateMatch } from './hooks/useReconApi'
import type { Transaction } from './types'

interface PostTabProps {
  transaction: Transaction
  clientId: string
  bankAccountId: number
  bankAccountLabel: string
}

export function PostTab({ transaction, clientId, bankAccountId }: PostTabProps) {
  const { data: accounts = [], isLoading } = useAccounts(clientId)
  const createMatch = useCreateMatch()
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [note, setNote] = useState(transaction.description || '')
  const [apxValues, setApxValues] = useState<Record<string, string>>({})
  const [apxPartStr, setApxPartStr] = useState('')

  const filtered = useMemo(() => {
    const nonBank = accounts.filter(
      (a) => !['500', '501', '502', '503', '506', '509'].includes(a.code)
    )
    if (!search) return nonBank
    const q = search.toLowerCase()
    return nonBank.filter(
      (a) =>
        a.code.toLowerCase().includes(q) ||
        (a.sub_code && a.sub_code.toLowerCase().includes(q)) ||
        a.name.toLowerCase().includes(q)
    )
  }, [accounts, search])

  const selectedAccount = accounts.find((a) => a.id === selectedAccountId)
  const txnAmount = Math.abs(transaction.amount)
  const isOutgoing = transaction.amount < 0

  const handleConfirm = () => {
    if (!selectedAccount) return

    const counterpart = {
      counterpart_kind: 'gl_account_post' as const,
      dt_account_id: isOutgoing ? selectedAccount.id : bankAccountId,
      ct_account_id: isOutgoing ? bankAccountId : selectedAccount.id,
      amount: txnAmount.toFixed(4),
      note: note.slice(0, 200) || undefined,
      doc_date: transaction.date || undefined,
      dt_apx_part: isOutgoing && selectedAccount.has_analytic && apxPartStr ? apxPartStr : undefined,
      ct_apx_part: !isOutgoing && selectedAccount.has_analytic && apxPartStr ? apxPartStr : undefined,
    }

    createMatch.mutate({
      transaction_id: transaction.id,
      client_id: clientId,
      mirage_bank_account_id: bankAccountId,
      match_kind: 'gl_post',
      counterparts: [counterpart],
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-neutral-400">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading accounts...
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <Input
          placeholder="Search accounts by code or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="max-h-[200px] overflow-y-auto rounded border border-neutral-200">
        {filtered.length === 0 ? (
          <div className="p-4 text-center text-sm text-neutral-400">
            No matching accounts found.
          </div>
        ) : (
          <div className="divide-y divide-neutral-100">
            {filtered.map((acc) => (
              <button
                key={acc.id}
                type="button"
                className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-blue-50 ${
                  selectedAccountId === acc.id ? 'bg-blue-50 font-medium' : ''
                }`}
                onClick={() => {
                  setSelectedAccountId(acc.id)
                  setApxValues({})
                  setApxPartStr('')
                }}
              >
                <span className="font-mono text-neutral-700">
                  {acc.code}{acc.sub_code ? `.${acc.sub_code}` : ''}
                </span>
                <span className="flex-1 text-neutral-700">{acc.name}</span>
                {acc.has_analytic && (
                  <span className="text-xs text-amber-600">has analytics</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedAccount && (
        <>
          {selectedAccount.has_analytic && (
            <APXFieldsEditor
              clientId={clientId}
              accountId={selectedAccount.id}
              values={apxValues}
              onChange={(vals, partStr) => {
                setApxValues(vals)
                setApxPartStr(partStr)
              }}
            />
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-500">
              Description / основание
            </label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={200}
              className="text-sm"
            />
            <span className="text-xs text-neutral-400">{note.length}/200</span>
          </div>

        </>
      )}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="blue"
          disabled={!selectedAccount || createMatch.isLoading}
          onClick={handleConfirm}
        >
          {createMatch.isLoading ? 'Creating...' : 'Confirm post'}
        </Button>
        {createMatch.isError && (
          <span className="text-sm text-red-600">
            {(createMatch.error as Error).message}
          </span>
        )}
      </div>
    </div>
  )
}
